import { supabase } from '../lib/supabase';

/**
 * WhatsApp Notification Service
 * Handles sending WhatsApp notifications through multiple providers
 */
export const whatsappNotificationService = {
  /**
   * Send WhatsApp notification via universal webhook
   */
  async sendNotification(phoneNumber, message, provider = 'n8n', shipmentId = null) {
    try {
      // Step 1: Create notification record with pending status
      const { data: notification, error: createError } = await supabase?.from('whatsapp_notifications')?.insert({
          shipment_id: shipmentId,
          phone_number: phoneNumber,
          message: message,
          status: 'pending',
          provider: provider
        })?.select()?.single();

      if (createError) throw createError;

      // Step 2: Get webhook configuration for provider
      const { data: webhookConfig, error: configError } = await supabase?.from('whatsapp_webhook_config')?.select('*')?.eq('provider', provider)?.eq('is_active', true)?.single();

      // If no webhook configured, update to failed
      if (configError || !webhookConfig) {
        await this.updateNotificationStatus(notification?.id, 'failed', {
          error: 'Webhook configuration not found or inactive'
        });
        return {
          success: false,
          notification: notification,
          error: 'Webhook not configured'
        };
      }

      // Step 3: Prepare webhook payload
      const payload = {
        phone: phoneNumber,
        message: message,
        shipment_id: shipmentId,
        provider: provider,
        timestamp: new Date()?.toISOString(),
        webhook_source: 'ADT Tracking'
      };

      // Step 4: Send to webhook (mock for now)
      try {
        // In production, replace with actual fetch call:
        // const response = await fetch(webhookConfig.webhook_url, {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //     'Authorization': `Bearer ${webhookConfig.api_key}`
        //   },
        //   body: JSON.stringify(payload)
        // });

        // Mock success response
        const mockResponse = {
          success: true,
          message_id: `mock_${Date.now()}`,
          provider: provider,
          timestamp: new Date()?.toISOString()
        };

        // Update notification as sent
        await this.updateNotificationStatus(notification?.id, 'sent', mockResponse);

        return {
          success: true,
          notification: notification,
          response: mockResponse
        };
      } catch (webhookError) {
        // Update notification as failed
        await this.updateNotificationStatus(notification?.id, 'failed', {
          error: webhookError?.message
        });

        return {
          success: false,
          notification: notification,
          error: webhookError?.message
        };
      }
    } catch (error) {
      console.error('WhatsApp notification error:', error);
      throw error;
    }
  },

  /**
   * Update notification status
   */
  async updateNotificationStatus(notificationId, status, webhookResponse = null) {
    try {
      const updateData = {
        status: status,
        updated_at: new Date()?.toISOString()
      };

      if (status === 'sent') {
        updateData.sent_at = new Date()?.toISOString();
      }

      if (webhookResponse) {
        updateData.webhook_response = webhookResponse;
      }

      const { data, error } = await supabase?.from('whatsapp_notifications')?.update(updateData)?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update notification status error:', error);
      throw error;
    }
  },

  /**
   * Get all notifications with optional filters
   */
  async getNotifications(filters = {}) {
    try {
      let query = supabase?.from('whatsapp_notifications_analytics')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('notification_status', filters?.status);
      }

      if (filters?.provider) {
        query = query?.eq('provider', filters?.provider);
      }

      if (filters?.shipmentId) {
        query = query?.eq('shipment_id', filters?.shipmentId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get notifications error:', error);
      throw error;
    }
  },

  /**
   * Retry failed notification
   */
  async retryNotification(notificationId) {
    try {
      // Get notification details
      const { data: notification, error: fetchError } = await supabase?.from('whatsapp_notifications')?.select('*')?.eq('id', notificationId)?.single();

      if (fetchError) throw fetchError;

      // Resend notification
      return await this.sendNotification(
        notification?.phone_number,
        notification?.message,
        notification?.provider,
        notification?.shipment_id
      );
    } catch (error) {
      console.error('Retry notification error:', error);
      throw error;
    }
  },

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase?.from('whatsapp_notifications')?.delete()?.eq('id', notificationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  },

  /**
   * Get webhook configurations
   */
  async getWebhookConfigs() {
    try {
      const { data, error } = await supabase?.from('whatsapp_webhook_config')?.select('*')?.order('provider');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get webhook configs error:', error);
      throw error;
    }
  },

  /**
   * Update webhook configuration
   */
  async updateWebhookConfig(provider, config) {
    try {
      const { data, error } = await supabase?.from('whatsapp_webhook_config')?.update({
          webhook_url: config?.webhookUrl,
          api_key: config?.apiKey,
          is_active: config?.isActive,
          config_data: config?.configData,
          updated_at: new Date()?.toISOString()
        })?.eq('provider', provider)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update webhook config error:', error);
      throw error;
    }
  },

  /**
   * Get message templates
   */
  async getMessageTemplates() {
    try {
      const { data, error } = await supabase?.from('whatsapp_message_templates')?.select('*')?.order('shipment_status');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get message templates error:', error);
      throw error;
    }
  },

  /**
   * Update message template
   */
  async updateMessageTemplate(status, messageFr, messageEn) {
    try {
      const { data, error } = await supabase?.from('whatsapp_message_templates')?.update({
          message_fr: messageFr,
          message_en: messageEn,
          updated_at: new Date()?.toISOString()
        })?.eq('shipment_status', status)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update message template error:', error);
      throw error;
    }
  },

  /**
   * Get notification statistics
   */
  async getNotificationStats(dateFrom = null, dateTo = null) {
    try {
      let query = supabase?.from('whatsapp_notifications')?.select('status, provider, created_at');

      if (dateFrom) {
        query = query?.gte('created_at', dateFrom);
      }

      if (dateTo) {
        query = query?.lte('created_at', dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate statistics
      const stats = {
        total: data?.length,
        sent: data?.filter(n => n?.status === 'sent')?.length,
        pending: data?.filter(n => n?.status === 'pending')?.length,
        failed: data?.filter(n => n?.status === 'failed')?.length,
        byProvider: {}
      };

      // Group by provider
      data?.forEach(notification => {
        if (!stats?.byProvider?.[notification?.provider]) {
          stats.byProvider[notification.provider] = {
            total: 0,
            sent: 0,
            pending: 0,
            failed: 0
          };
        }
        stats.byProvider[notification.provider].total++;
        stats.byProvider[notification.provider][notification.status]++;
      });

      return stats;
    } catch (error) {
      console.error('Get notification stats error:', error);
      throw error;
    }
  }
};

export default whatsappNotificationService;